<template>
  <div id="vote">
    <!-- FT-1075: `vo-live` marks the hands for exactly as long as the sweep
         runs — it is the class the gate block's z-order reads to decide who
         stands in front, the hands (sweeping) or the card (standing). -->
    <div class="arrows" :class="{ 'vo-live': isDocked }">
      <div class="needle needle-nominator" :style="nominatorStyle"></div>
      <div class="needle needle-nominee" :style="nomineeSweepStyle"></div>
    </div>
    <!-- Golem fork (FT-976): THE NOMINATION OVERLAY, in the app's own chrome.
         Three things were wrong with the old one, and only one of them was
         cosmetic.

         1. THE FINISH CONTROL LIED. It read "Close" in `demon` red, and in
            this fork red means LIT — `control-lit`, a control that is ON. So
            the one button that COMMITS the nomination to the vote log was
            dressed as the one that throws it away. See `finishLabel` below:
            it now says which of the two things it is about to do, because it
            genuinely does both depending on state.
         2. THE COUNT WAS THE HARDEST THING ON SCREEN TO READ. White and
            saturated blue type sat straight on the painted rose window at
            1.14:1 measured contrast — a mid-tone ochre behind a mid-luminance
            blue. The black text-shadow it wore does nothing against a ground
            that is already dark-ish. It now has a ground of its own and is
            the headline it always should have been.
         3. FOUR CONTROLS WERE TWO. "Mark for execution" + "Clear mark" is one
            piece of state, and so is Hand DOWN / Hand UP. This app already
            has the shapes for both — `control-toggle` and the
            plate+`control-cell` segment — and `NumberScrub` for the timing.

         THE GROUND, IN TWO REGISTERS (FT-1024, user call — superseding
         FT-976's "a scrim, not the face disc"): on desktop the overlay now
         STANDS ON THE FACE DISC, the same plate the night checklist and the
         host panel wear (src/faceDisc.scss — geometry, gate, material).
         Below the gate — every phone, every small window — the scrim below
         remains the ground exactly as FT-976 built it: the plate's own
         ground colour taken to near-opaque at the centre and faded to
         nothing at the rim, an edgeless ground for a circle's middle. The
         disc is a desktop dress; the re-grounding moves NO control.

         FT-1074 — THE CARD LEARNS ITS PLACE. Two passes in one:
         (a) HIERARCHY. The nomination line is small at the top, the tally is
             the hero with "majority N" as its quiet subline, and the buttons
             wear three distinct weights: ONE primary Start (the old
             "Countdown" and "Start" pair folded — see countdown() below),
             the execution mark with its noose, and a small quiet Cancel.
             "Time per player" collapsed from a labeled row to a tiny scrub
             sitting beside the primary button.
         (b) THE DOCK. Once the vote runs — the hands sweeping — the card
             SHRINKS AND SLIDES to the bottom of the circle as a compact
             strip (tally + the controls legal mid-vote), so the hands sweep
             over an open face with no text under the pivot. Transform and
             opacity only, `mode="out-in"`: the card shrinks away downward,
             the strip rises into its dock. When the sweep ends (or Reset is
             pressed) the same motion runs in reverse. -->
    <!-- FT-1311 (real-game note: "the countdown is too quiet"): the clip is
         now countdown-loud.mp3 — the same take normalized from a measured
         max of -14.7dB up to -1.1dB (claude_temp_test/2026-08-29-ft1311-*).
         The element was already playing at volume 1.0, the browser's own
         ceiling, so the only lever left was the asset itself; the bells go
         through towerBells' volume dial (element.volume = percent/100),
         which can only ever make a sound QUIETER than its file. The quiet
         original stays in assets, stood down.

         FT-1436 (user, 2026-09-11): the clip is now vote-countdown.mp3 —
         the user's own three-strike bell countdown (strikes at 1 / 2 / 3 s,
         so the sweep starts on the fourth beat), trimmed to 4.6 s and
         levelled to the same -1 dB peak. The borrowed countdown files are
         deleted (user, 2026-09-11).

         FT-1377 (real-game report: "the countdown sound keeps playing"):
         this preloader is now the ONE element that plays. The countdown
         block below used to carry its own `:autoplay` twin, which replayed
         on every remount of that block — and the block remounts on every
         return to `lockedVote 0` while a vote is in progress, whether or
         not a new countdown genuinely started. Playback is explicit now:
         playCountdownOnce() below fires on the RISING EDGE of the
         countdown phase (isCountdownPhase, false → true), so one vote
         start is one play — a deliberate "Restart the vote" is a new
         edge and rightly replays; a remount alone no longer can. -->
    <audio
      ref="countdownAudio"
      src="../assets/sounds/vote-countdown.mp3"
      preload="auto"
    ></audio>
    <!-- FT-1324 (user correction, reverting FT-1311 item 3): that pass put
         a seated player's Hand UP / Hand DOWN pair in one fixed strip at the
         dock slot for both phases — the WRONG location. Restored to where
         it lived before 74e121b: the pair rides the vo-dock transition with
         everything else, in the card while the nomination is open and in
         the docked strip while the vote runs. That does mean it can jump
         between the two the way it did pre-1311 — the card and the strip
         are different containers by construction, so there is no "old
         location" that is also jump-free. What FT-1311 item 3 fixed WITHIN
         each location stays: once the sweep passes my seat (or I never had
         a vote to raise) the pair freezes in place — dimmed, `is-locked`
         below — instead of vanishing and reflowing.

         FT-1377 OVERRULES the jump (user call, from live games): on a
         PLAYER client the pair must sit mid-face and never move from where
         it starts. The mechanism is one flag, not new markup: isDocked is
         storyteller-only now, so for a player this transition simply never
         fires — the card (and the pair on it) stands at the face centre
         through nomination, countdown, sweep and lock, and the docked
         strip below is the storyteller's alone. -->
    <!-- FT-1382 (user, live vet): a SEATED PLAYER sees ONLY the hands —
         no card, no disc plate, no nomination line, no tally. The card
         below is the storyteller's and the unseated watcher's; the
         player's whole vote surface is the bare pair in .vo-solo further
         down, fixed at the face centre. -->
    <transition name="vo-dock" mode="out-in">
      <div
        class="overlay"
        v-if="!isDocked && !(session.isSpectator && player)"
        key="card"
      >
        <p class="vo-nomination">
          <em class="blue">{{ nominator.name }}</em> nominated
          <em>{{ nominee.name }}</em>
        </p>

        <!-- THE HEADLINE. The running count against the majority is the live
             state of the nomination and the thing the whole room is watching,
             so it is the biggest thing here rather than one more line of body
             text. `is-majority` lights it with `control-lit`'s own colours the
             moment the count reaches the bar — the same "this is ON" language
             every other lit control in the app speaks. -->
        <div class="vo-tally" :class="{ 'is-majority': hasMajority }">
          <div class="vo-count" data-tally>
            <span class="vo-now">{{ voters.length }}</span>
            <span class="vo-slash">/</span>
            <span class="vo-need">{{ majority }}</span>
          </div>
          <div class="vo-caption">majority {{ majority }}</div>
        </div>
        <template v-if="!session.isSpectator">
          <div class="vo-controls vo-start-row">
            <!-- Primary while starting is the decisive act; once a completed
                 sweep is waiting to be recorded, "Record vote" below takes
                 the emphasis and this demotes to a plain plate — never two
                 primaries on one card. -->
            <!-- FT-1378 (user): the seconds live INSIDE the button, at the
                 label's own size — one control reading "Start the vote · 3 s"
                 where the number is still the scrub. Its span swallows the
                 click so a scrub gesture never starts the vote. -->
            <button
              class="vo-btn vo-start"
              :class="{ 'is-primary': !willRecord }"
              @click="countdown"
            >
              {{ session.lockedVote ? "Restart the vote" : "Start the vote" }}
              <span
                class="vo-timing vo-timing-in"
                v-if="session.lockedVote < 1"
                @click.stop
                title="Time per player — seconds each seat gets before the sweep moves on"
              >
                <NumberScrub
                  class="vo-scrub"
                  :value="votingSeconds"
                  :min="0.5"
                  :max="30"
                  :step="0.5"
                  aria-label="Time per player, in seconds"
                  title="Time per player, in seconds — drag sideways to scrub, click to type"
                  @input="setVotingSeconds"
                />
                <span class="vo-unit">s</span>
              </span>
            </button>
            <!-- FT-1331 (user correction, reverting FT-1325): the vote
                 timer is a STORYTELLER control and its home is THIS card —
                 FT-1325 read "move the timer options to settings" as this
                 scrub, but the user meant the toolbar Timer menu's display
                 options (FT-1333 moves those). Restored verbatim from
                 7200a49's removal: the app's own number control, whole and
                 half seconds (FT-1311 item 5), handing setVotingSeconds the
                 delta that keeps the store in milliseconds. The pace the
                 room is about to live under is readable right here, beside
                 the button that starts it. -->
          </div>

          <div class="vo-controls">
            <button
              class="vo-btn vo-finish"
              :class="{ 'is-primary': willRecord }"
              :title="finishTitle"
              @click="finish"
            >
              {{ finishLabel }}
            </button>
          </div>
        </template>

        <p class="vo-hint" v-else>
          Please claim a seat to vote.
        </p>
      </div>

      <!-- THE DOCKED STRIP (FT-1074) — the card's mid-vote form. Tally big,
           plus only what is legal while the hands sweep: Pause/Resume and
           Reset for the storyteller, my own hand for a seated player. It
           stands on a plain control plate rather than the disc or the scrim —
           a strip at the rim wants an edge, not a halo. -->
      <!-- FT-1382: v-else-if, not v-else — the strip is the STORYTELLER's
           docked form only; a seated player (whose card v-if is false too)
           gets the bare .vo-solo pair below instead of falling in here. -->
      <div class="overlay vo-docked" v-else-if="isDocked" key="strip">
        <div class="vo-tally" :class="{ 'is-majority': hasMajority }">
          <div class="vo-count" data-tally>
            <span class="vo-now">{{ voters.length }}</span>
            <span class="vo-slash">/</span>
            <span class="vo-need">{{ majority }}</span>
          </div>
        </div>
        <template v-if="!session.isSpectator">
          <button
            class="vo-btn"
            :class="{ disabled: !session.lockedVote }"
            @click="pause"
          >
            {{ voteTimer ? "Pause" : "Resume" }}
          </button>
          <button class="vo-btn" @click="stop">Reset</button>
        </template>
      </div>
    </transition>
    <!-- FT-1382 (user): THE PLAYER'S WHOLE VOTE SURFACE — the bare pair at
         the face centre, no card, no disc, no tally. Outside every
         transition on purpose: it mounts with the nomination and never
         moves or animates (FT-1377's zero-move rule kept by construction). -->
    <div
      class="vo-solo"
      v-if="session.isSpectator && player"
      role="group"
      aria-label="Your vote"
      :class="{ 'is-locked': !canVote }"
      :title="
        canVote ? '' : 'Your vote is locked in — the sweep has passed your seat'
      "
    >
      <div class="vo-hands" :class="{ 'is-locked': !canVote }">
        <button
          class="vo-hand"
          :class="{ on: !currentVote }"
          :aria-pressed="String(!currentVote)"
          :aria-disabled="String(!canVote)"
          @click="vote(false)"
        >
          Hand DOWN
        </button>
        <button
          class="vo-hand"
          :class="{ on: !!currentVote }"
          :aria-pressed="String(!!currentVote)"
          :aria-disabled="String(!canVote)"
          @click="vote(true)"
        >
          <span class="vo-hand-ic vo-hand-nom" aria-hidden="true"></span>
          Hand UP
        </button>
      </div>
    </div>
    <!-- THE COUNTDOWN, IN THE TOWER'S OWN NUMERALS (FT-1083, user call).
         Upstream counted in big saturated blue-then-red arabic digits that
         belonged to no other surface in this fork, and drew them BEHIND the
         clock hands. Both are answered here.

         THE NUMERALS. Three beats, three roman numerals — III, II, I — in
         the dial's own lettering: Times bold with FaceHands' `.tw-numeral`
         dressing (the FT-1064 bake, still reading its `--ng-*` dials so the
         ring and the countdown can never drift apart). Since FT-1438 (user)
         they wear the ring's own near-black ink too — the clock's numerals
         counting down — and they show ONLY before the sweep: FT-1331's
         per-seat clock during the sweep is retired.

         GO KEEPS ITS BEAT and its heat — same material, the blood ink this
         app already spends on the decisive act, and the only beat that grows
         on the way out rather than settling.

         THE ORDER — the user's explicit ask, "the numbers should be above
         the hands". `.countdown` takes `z-index: 3`, which clears BOTH the
         overlay's 1 and `.arrows.vo-live`'s 2 (the FT-1075 gate rule below).
         It disturbs nothing that rule governs: the countdown exists for
         exactly the three seconds `isVoteInProgress && !lockedVote` is true —
         after Start, before the first hand locks — the card is already docked
         at the rim by then, and this layer takes no clicks. -->
    <transition name="blur">
      <div class="countdown" v-if="isCountdownPhase">
        <span class="vo-beat">III</span>
        <span class="vo-beat">II</span>
        <span class="vo-beat">I</span>
        <span class="vo-beat vo-beat-go">GO</span>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
// FT-1379: the viewer's own vote-start volume dial (Sounds section).
import { prefsState } from "../golem/prefs";
// FT-1331: back with the restored timer control — the same scrub the seat
// count and the night sheet's numbers use (removed by 7200a49/FT-1325,
// which the user has since corrected: the timer belongs on this card).
import NumberScrub from "./NumberScrub";

/** FT-1331: the sweep clock counts in the dial's own numerals, like the
 *  III-II-I beats. The timer setting runs 0.5–30s (PlayerSettings' scrub),
 *  so tens are enough. */
// The III-II-I-GO overture's length: four 1s beats (see the style block).
const OVERTURE_MS = 4000;
// How much sooner than a full pace step the nominee's hand arrives, so it
// rests on each seat for a breath before that seat locks.
const GLIDE_LEAD_MS = 120;

/** Where a seat falls in the sweep: 0 is the seat after the nominee, the
 *  nominee itself comes last. The same formula socket.js's _handleVote uses
 *  to accept or refuse a hand, so button and receiver agree seat for seat. */
function sweepSlot(seat, nominee, count) {
  return (seat - nominee - 1 + count) % count;
}

export default {
  components: { NumberScrub },
  computed: {
    ...mapState({
      players: (state) => state.players.players,
      session: "session",
      grimoire: "grimoire",
    }),
    ...mapGetters({ alive: "players/alive" }),
    /** A traveler is EXILED, not executed — no execution mark, and the
     *  majority is counted off the whole table rather than the living. Both
     *  rules were already here, inline and stated twice; this names them
     *  once. */
    isExile: function() {
      return this.nominee.role.team === "traveler";
    },
    /** FT-1074: the card docks — shrinks to the strip at the bottom of the
     *  circle — for exactly as long as the sweep runs. `setVoteInProgress`
     *  is flipped on by countdown()/start() and off by stop(), by the sweep
     *  completing, and (for everyone) by the relay's own sync, so the strip
     *  returns to the full card the moment the hands stand still.
     *
     *  FT-1377 (user call, overruling FT-1324's restore): the dock is the
     *  STORYTELLER'S alone now. A seated player's card — and the Hand
     *  UP / Hand DOWN pair on it — holds the centre of the face for the
     *  whole vote: nomination open, countdown, sweeping, locked. On a
     *  player client this is simply never true, so the card never leaves,
     *  nothing remounts, and the pair cannot move from where it starts.
     *  (FT-1324 accepted the card↔strip jump as the price of the pair
     *  living in both containers; the user has since ruled the pair's
     *  position outranks the dock.) `vo-live` rides this same flag, so on
     *  a player client the sweeping hands stay UNDER the standing card —
     *  a scarlet shaft through the tally was the exact unreadability
     *  FT-976 fixed; the tips still reach the seats unobscured. */
    isDocked: function () {
      return this.session.isVoteInProgress && !this.session.isSpectator;
    },
    /** FT-1331 (restored from 7200a49's removal): the scrub's read — the
     *  store keeps milliseconds, the control speaks whole and half seconds. */
    votingSeconds: function () {
      return this.session.votingSpeed / 1000;
    },
    /** FT-1377: the three seconds the III-II-I beats (and their sound) own —
     *  after Start, before the first hand locks. The WATCHER on this is the
     *  countdown clip's one trigger: it plays on the rising edge and only
     *  there, so remounts of the beat block can never replay it. */
    isCountdownPhase: function () {
      return this.session.isVoteInProgress && !this.session.lockedVote;
    },
    /** The bar this nomination has to clear. Same arithmetic the vote log
     *  itself uses when it records the result (session/addHistory), so the
     *  number on screen and the number in the history can never disagree. */
    majority: function() {
      return Math.ceil((this.isExile ? this.players.length : this.alive) / 2);
    },
    hasMajority: function() {
      return this.voters.length >= this.majority;
    },
    /**
     * WHAT THE FINISH CONTROL IS ABOUT TO DO — and it is genuinely two
     * different things, which is why the old single word for it was unsafe in
     * both directions.
     *
     * `finish()` always clears the nomination, but the history entry is
     * written by `session/addHistory`, which returns early unless the sweep
     * ran all the way round:
     *
     *     if (!state.nomination || state.lockedVote <= players.length) return;
     *
     * So on a COMPLETED vote the control records the result; on an unstarted
     * or half-swept one it silently discards the nomination and records
     * nothing. "Close" could be read either way, and was wrong either way —
     * a storyteller expecting a discard got a permanent log entry, and one
     * expecting a record got nothing. The label follows the state instead.
     */
    willRecord: function() {
      return this.session.lockedVote > this.players.length;
    },
    finishLabel: function() {
      return this.willRecord ? "Record vote" : "Cancel nomination";
    },
    finishTitle: function() {
      return this.willRecord
        ? "Write this result to the vote history and end the nomination"
        : "End the nomination without recording it — the vote did not finish";
    },
    nominator() {
      const [from] = this.session.nomination;
      return this.players[from];
    },
    /**
     * OFF BY ONE SEAT until 2026-08-20 (user report, with the right diagnosis:
     * "possibly because we moved the 1 position to be where it is on a clock
     * instead of at the 12 position").
     *
     * The ring puts seat `i` at `((i + 1) * 360) / count` — see TownSquare's
     * own placement — so seat 0 sits ONE STEP PAST twelve o'clock, the way a
     * clock's 1 does. These arrows were computing `(i / count) * 360`, which
     * is the same ring MINUS that step, so every arrow pointed at the seat
     * before its target. It was invisible while both were wrong together and
     * became visible the moment anyone checked one against a real seat.
     *
     * `+ 1` here rather than a shared helper: the ring's own placement is CSS
     * in the on-circle mixin and this is an inline transform, so there is no
     * single expression the two could share without inventing one. The two
     * sites are named in each other's comments instead.
     */
    nominatorStyle() {
      const players = this.players.length;
      const nomination = this.session.nomination[0];
      return {
        transform: `rotate(${Math.round(((nomination + 1) / players) * 360)}deg)`,
        transitionDuration: this.handGlide,
      };
    },
    nominee() {
      const [, to] = this.session.nomination;
      return this.players[to];
    },
    nomineeSweepStyle() {
      const players = this.players.length;
      const nomination = this.session.nomination[1];
      const lock = this.session.lockedVote;
      // `+ 1` for the ring's own offset (see nominatorStyle above); `lock` is
      // how far the vote has swept past the nominee, and is unrelated.
      const rotation =
        (360 * (nomination + 1 + Math.min(lock, players))) / players;
      return {
        transform: `rotate(${Math.round(rotation)}deg)`,
        transitionDuration: this.handGlide,
      };
    },
    /** One pace step less a breath — the hands glide the whole interval. */
    handGlide() {
      return `${this.session.votingSpeed - GLIDE_LEAD_MS}ms`;
    },
    /** This client's seat index, or -1 when it holds none. */
    mySeat() {
      const me = this.session.playerId;
      return this.players.findIndex(({ id }) => id === me);
    },
    player() {
      return this.mySeat < 0 ? undefined : this.players[this.mySeat];
    },
    currentVote() {
      if (this.mySeat < 0) return undefined;
      return Boolean(this.session.votes[this.mySeat]);
    },
    canVote() {
      const seat = this.mySeat;
      if (seat < 0) return false;
      if (this.players[seat].isVoteless && !this.isExile) return false;
      const slot = sweepSlot(
        seat,
        this.session.nomination[1],
        this.players.length
      );
      return slot >= this.session.lockedVote - 1;
    },
    /** Names of the raised hands, in sweep order. Once the sweep runs only
     *  the seats already locked count; before it, every raised hand does. */
    voters() {
      const count = this.players.length;
      const nominee = this.session.nomination[1];
      const lock = this.session.lockedVote;
      const counted = lock >= 1 ? Math.min(lock - 1, count) : count;
      const names = [];
      for (let slot = 0; slot < counted; slot++) {
        const seat = (nominee + 1 + slot) % count;
        if (!this.session.votes[seat]) continue;
        const { name } = this.players[seat];
        if (name) names.push(name);
      }
      return names;
    },
  },
  data() {
    return {
      voteTimer: null,
    };
  },
  watch: {
    // FT-1377: the countdown clip's one trigger — see isCountdownPhase.
    isCountdownPhase: "playCountdownOnce"
  },
  beforeDestroy() {
    // FT-1377: the vote timer must not outlive the component. A nomination
    // can close under a live interval (a nominated seat removed mid-vote
    // aborts the nomination from TownSquare, unmounting this component),
    // and an interval left running here keeps committing lock/progress
    // state against a vote that no longer exists.
    clearInterval(this.voteTimer);
  },
  methods: {
    /** THE ONE START (FT-1074). The card used to carry two pre-vote buttons —
     *  "Countdown" (this method: 3-2-1-GO, then the sweep) and "Start" (the
     *  sweep immediately, no countdown). One primary control starts a vote
     *  now, and it is THIS flow, because the countdown is the version the
     *  room can follow — every player gets the same three seconds of warning
     *  before the first hand locks. start() below is unchanged and is now
     *  reached only as this countdown's own completion. */
    countdown() {
      // FT-1377 — THE REMOUNT CULPRIT, found by rig (claude_temp_test/
      // 2026-09-04-ft1377-audio-diagnose.log, phase 9): this method used to
      // assign voteTimer WITHOUT clearing what it held, so a second press
      // before the countdown ended (the button stays clickable for the
      // ~240ms dock transition) ORPHANED the pending 4s interval. The
      // orphan then called start() every 4 seconds forever: the sweep
      // reset to seat 1 on each firing, isVoteInProgress was re-asserted
      // even after the nomination closed, and every recovery press
      // remounted the old autoplay block — "the countdown sound keeps
      // playing". One clearInterval makes a double press idempotent.
      clearInterval(this.voteTimer);
      // Count to 0 BEFORE "in progress": a player's screen must never pair
      // the new vote with the last vote's lock count.
      this.$store.commit("session/lockVote", 0);
      this.$store.commit("session/setVoteInProgress", true);
      this.voteTimer = setTimeout(this.start, OVERTURE_MS);
    },
    start() {
      this.$store.commit("session/lockVote", 1);
      this.$store.commit("session/setVoteInProgress", true);
      clearInterval(this.voteTimer);
      this.tickSweep();
    },
    /** Lock one more seat every pace step until every seat has had its
     *  turn, then stand the vote down. The pace is read once, here. */
    tickSweep() {
      const pace = this.session.votingSpeed;
      this.voteTimer = setInterval(() => {
        this.$store.commit("session/lockVote");
        if (this.session.lockedVote <= this.players.length) return;
        clearInterval(this.voteTimer);
        this.voteTimer = null;
        this.$store.commit("session/setVoteInProgress", false);
      }, pace);
    },
    pause() {
      if (this.voteTimer) {
        clearInterval(this.voteTimer);
        this.voteTimer = null;
      } else {
        this.tickSweep();
      }
    },
    /**
     * FT-1377: ONE vote start, ONE play. Fired by the isCountdownPhase
     * watcher on its rising edge only — the moment Start (or a deliberate
     * Restart) takes the vote to `in progress, nothing locked`. The clip
     * plays from its top on the always-mounted preloader element, so a
     * remount of the beat block — the old `:autoplay` trigger — plays
     * nothing. Mute is honored at the moment of the edge, the same gate
     * the old `:autoplay="!grimoire.isMuted"` binding applied.
     */
    playCountdownOnce(entering) {
      if (!entering || this.grimoire.isMuted) return;
      const el = this.$refs.countdownAudio;
      if (!el) return;
      el.currentTime = 0;
      // FT-1378 (user): ~20% down from the FT-1311 normalization.
      // FT-1379: the trim is the viewer's own dial now — the Sounds
      // section's vote-start slider (default 80 keeps FT-1378's level).
      el.volume = prefsState.volVoteStart / 100;
      const p = el.play();
      // Autoplay policy can refuse (a player who has never touched the
      // page); a refused play must not become an unhandled rejection.
      if (p && p.catch) p.catch(() => {});
    },
    stop() {
      clearInterval(this.voteTimer);
      this.voteTimer = null;
      // "In progress" off BEFORE the count returns to 0 — the reverse order
      // would read as a fresh countdown on every player's screen.
      this.$store.commit("session/setVoteInProgress", false);
      this.$store.commit("session/lockVote", 0);
    },
    finish() {
      clearInterval(this.voteTimer);
      this.voteTimer = null;
      // History first: it (and the chronicle writer) read the nomination
      // the second commit clears.
      this.$store.commit("session/addHistory", this.players);
      this.$store.commit("session/nomination");
    },
    vote(raised) {
      if (!this.canVote) return;
      const seat = this.mySeat;
      if (seat < 0 || Boolean(this.session.votes[seat]) === raised) return;
      // voteSync, never session/vote: only the synced twin reaches the room.
      this.$store.commit("session/voteSync", [seat, raised]);
    },
    /** FT-1331 (restored with the scrub): whole/half seconds in, the same
     *  millisecond delta the original stepper fed the store. */
    setVotingSpeed(diff) {
      const speed = Math.round(this.session.votingSpeed + diff);
      if (speed > 0) {
        this.$store.commit("session/setVotingSpeed", speed);
      }
    },
    setVotingSeconds(seconds) {
      this.setVotingSpeed(seconds * 1000 - this.session.votingSpeed);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../vars.scss";
@import "../controls.scss";
@import "../faceDisc.scss";
#vote {
  // No insets: App.vue's flex centring places this box on the viewport's
  // centre, which the disc frame and the dock offset below both rely on.
  position: absolute;
  width: 20%;
  z-index: 20;
  // FT-1074: how far below the ring's centre the docked strip sits. #vote is
  // flex-centred on #app with no insets, so its box centre IS the viewport
  // centre (the disc-frame comment below walks through why); 50vh is then the
  // circle's southern rim and the em term backs the strip off it far enough
  // to clear the bottom seats' tokens. Read by the strip's own transform AND
  // by both dock transitions, so the three always agree on where "docked" is.
  --vo-dock-y: calc(50vh - 8.5em);
  display: flex;
  place-content: center;
  place-items: center;
  background: url("../assets/demon-head.png") center / auto 74% no-repeat;
  text-align: center;

  // A zero-width flex item as tall as the box is wide: the box stays square,
  // which the hands' 150%-of-height frame assumes.
  &::before {
    content: "";
    padding-block-end: 100%;
  }

  em {
    font-style: normal;
    font-weight: 700;
    color: $demon;

    &.blue {
      color: $townsfolk;
    }
  }
}

// ── THE OVERLAY, AND ITS GROUND ─────────────────────────────────────────────
// The scrim. A radial fade rather than a bordered box, for the reason in the
// template comment: this sits in the middle of a circle. It is the plate's own
// ground colour ($control-bg is rgba(0,0,0,.7)) pushed to near-opaque where
// the type actually is, then let go entirely by the rim, so there is no edge
// anywhere for the eye to catch on.
//
// THE SCRIM MUST OUTRANK THE CLOCK HANDS — BELOW THE GATE. `.arrows` are
// absolutely positioned siblings at z-index auto, and they run at 150% height
// — the tips point out at the nominator's and nominee's seats, which is the
// whole job, but the SHAFTS cross the middle of the overlay, and a scarlet
// hand drawn straight through the count is exactly as unreadable as the ochre
// was. So `.overlay` takes a stacking context of its own ABOVE them
// (`z-index: 1`), and the scrim's `z-index: -1` is then scoped inside that
// context: it paints under every glyph here but over both hands.
//
// ON THE DISC THE ORDER REVERSES — DURING THE SWEEP (FT-1024b, user call:
// "the nomination hands should be on top"; FT-1075, user call, scoping it:
// "the buttons are still behind the clock hands"). Under the gate the ground
// is the plate's glass, not the scrim, and a hand dimmed to a third of its
// brightness by the plate's brightness(0.34) stopped doing its one job —
// saying WHO, visibly. So inside the gate block below, `.arrows.vo-live`
// takes `z-index: 2` and rides above the plate and its type both — but ONLY
// while the vote runs (`vo-live` is bound to `isDocked`): a standing hand
// lying across the Start button serves nobody, so the pre-vote card outranks
// the hands in both registers. The hands are `pointer-events: none`, so
// nothing under them ever loses a click. Below the gate this paragraph's
// original order stands untouched at all times.
.overlay {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.45em;
  padding: 1em 1.6em 1.15em;
  // wide enough that the nomination line and the widest control do not force
  // the scrim into a letterbox; the ring is 20% of the viewport and the
  // overlay has always been allowed to overhang it.
  width: 15em;

  &:before {
    content: "";
    position: absolute;
    // A CONSTANT halo, not a proportional one. As a percentage this shrank
    // with the box, and a player's overlay is barely half the height of the
    // storyteller's — the fade reached the type before it reached zero.
    inset: -1.7em -2.6em;
    z-index: -1;
    background: radial-gradient(
      ellipse at 50% 50%,
      rgba(0, 0, 0, 0.94) 0%,
      rgba(0, 0, 0, 0.92) 40%,
      rgba(0, 0, 0, 0.82) 60%,
      rgba(0, 0, 0, 0.5) 78%,
      rgba(0, 0, 0, 0) 100%
    );
    pointer-events: none;
  }
}

// ── THE DISC (FT-1024, user call — reversing FT-976's "not the face disc") ──
// On desktop the overlay stands ON the plate the night checklist and the host
// panel wear: same geometry, same gate, same material, all of it from
// src/faceDisc.scss. Below the gate nothing in this block applies and the
// scrim above is the ground, untouched — the disc is a desktop dress.
//
// WHY THE FRAME'S PERCENTAGES ARE HONEST FROM INSIDE #vote — because they are
// not resolved against #app here, and it matters. face-disc-frame positions
// with `--face-cx` = calc(50% + 7px + …), and that 50% resolves against the
// overlay's CONTAINING BLOCK, which is #vote (`position: absolute` above),
// not #app the way it is for NightSheet and HostTools. It still lands to the
// pixel, and not by luck: #vote is an absolutely-positioned flex child of
// #app with no insets, so its static position CENTRES its box on #app's
// centre in both axes — 50% of #vote's box IS #app's 50% — and every other
// term in the frame's expressions (the +7px, the geometry map's offsets, the
// lab's adjusts) is an absolute length, indifferent to the base. If #vote
// ever takes an inset or leaves #app's flex centring, this equality breaks
// and the disc drifts off the dial; the sweep rig for this lane
// (claude_temp_test/2026-08-21-ft1024-*) is what would catch it.
//
// THE SCRIM STANDS DOWN INSIDE THE GATE without a single property of its own
// being touched: face-disc-plate's ground layer redeclares every property the
// scrim's ::before sets (content, position, inset, z-index, background,
// pointer-events), and this block compiles after the base rule, so the
// plate's declarations win here and only here.
@include face-disc-gate {
  // FT-1074: `:not(.vo-docked)` — the disc is the FULL card's dress only. The
  // docked strip keeps its own plain plate in both registers; a face disc
  // shrunk to a strip at the rim would be neither a disc nor a strip.
  .overlay:not(.vo-docked) {
    @include face-disc-frame;
    // The frame is already a centring flex column; this overlay's furniture
    // is a compact cluster, not a header/band/foot spread, so it gathers at
    // the plate's centre instead of being distributed into the caps — every
    // control keeps its order and its logic. Re-grounding, not redesign.
    justify-content: center;

    // The card's dock travel, restated ON TOP of the frame's own centring
    // translate — the frame positions with `transform: translate(-50%,-50%)`,
    // and a transition class that replaced it would teleport the card to the
    // box's corner before animating. Same destination as the base rule below,
    // same var, one extra leading term.
    &.vo-dock-enter,
    &.vo-dock-enter-from,
    &.vo-dock-leave-to {
      opacity: 0;
      transform: translate(-50%, -50%) translateY(calc(var(--vo-dock-y) * 0.55))
        scale(0.6);
    }
  }

  // FT-1024b (user call): the pointer hands ride ABOVE the glass. The plate's
  // brightness(0.34) took the hands down to a third of their paint, and a
  // pointer that has to be looked for is not pointing. z-index 2 clears the
  // overlay's own 1, so the hands paint over the plate AND its type — ruled,
  // not drifted into; the full stacking story (and why the order is the
  // opposite below the gate) is the scrim block's own comment above.
  //
  // FT-1075 (user call: "the buttons are still behind the clock hands")
  // SCOPES that ruling to the sweep. `.vo-live` is bound to `isDocked` — true
  // for exactly as long as the vote runs. While the hands SWEEP, FT-1024b
  // stands whole: the card is the docked strip at the rim, the face is open,
  // and a hand saying WHO votes now must not be dimmed by anything. While the
  // hands STAND — the full card up, Start not yet pressed — they are only
  // marking two seats, their tips do that job outside the plate's footprint,
  // and a shaft lying across the Start button was costing a click's clarity
  // to decorate a control. So at rest the selector no longer matches, the
  // hands fall back to z-index auto, and the overlay's own 1 (the base-rule
  // order below the gate, unchanged since FT-976) puts the card in front.
  .arrows.vo-live {
    z-index: 2;
  }
}

// WHO NOMINATED WHOM — secondary now. It was the same weight as the count.
.vo-nomination {
  margin: 0;
  font-size: 0.62em;
  line-height: 1.35;
  color: #e7dfcd;
  // the names keep their team colours, which are mid-luminance hues that were
  // unreadable on ochre and are fine on the scrim
  text-shadow: 0 1px 2px #000;
}

// ── THE HEADLINE ────────────────────────────────────────────────────────────
.vo-tally {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;

  .vo-count {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.1em;
    font-weight: bold;
  }
  // FT-1074: the tally is the HERO now — the count grew half again and the
  // shouting "VOTES IN FAVOR · MAJORITY IS 5" caption retired into the quiet
  // "majority 5" subline the template renders.
  .vo-now {
    font-size: 3.1em;
    color: #f7f0e1;
  }
  .vo-slash {
    font-size: 1.5em;
    color: rgba(216, 205, 180, 0.45);
  }
  .vo-need {
    font-size: 1.7em;
    color: #d8cdb4;
  }
  .vo-caption {
    margin-top: 0.3em;
    font-size: 0.5em;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #c8bda6;
  }

  // MAJORITY REACHED. `control-lit`'s own colours — the same blood this app
  // lights every chosen control with — so the moment the bar is cleared
  // speaks the language the rest of the UI already speaks.
  //
  // THE NUMBERS LIGHT; THE CAPTION DOES NOT. `$control-on-edge` is a mid
  // rose that measures about 4:1 on this scrim, under the 4.5:1 that type
  // this small wants, and the caption is a fixed label rather than a piece
  // of state — lighting it dimmed the one line that has to stay readable in
  // order to explain the one that just changed.
  &.is-majority {
    .vo-now {
      color: $control-on-color;
      text-shadow: 0 0 18px rgba(190, 90, 90, 0.55);
    }
    .vo-need {
      color: $control-on-edge;
    }
  }
}

// ── ROWS AND CONTROLS ───────────────────────────────────────────────────────
// FT-1331: BACK IN SERVICE — FT-1325 stood these two down when it removed
// the card's scrub; the user corrected that call (the vote timer is a
// storyteller control and lives on this card), so the scrub and its dress
// are restored together.
.vo-scrub {
  color: #f7f0e1;
  &:hover {
    color: #fff;
  }
}
.vo-timing {
  display: inline-flex;
  align-items: baseline;
  font-size: 0.62em;
  color: #cfc4ad;
  .vo-unit {
    margin-left: 0.12em;
  }
}
// The scrub stays BESIDE the primary in both registers (load-bearing again —
// FT-1331 restored the scrub to this row).
.vo-controls.vo-start-row {
  flex-wrap: nowrap;
}

.vo-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4em;
  flex-wrap: wrap;
}

// A PLATED TEXT BUTTON — the ground, edge, radius and purple pointer
// acknowledgement every other control in this app wears (src/controls.scss).
// The pill with its radial gradients and its red hover was the last of the
// upstream chrome on this surface.
.vo-btn {
  @include control-plate;
  font-family: inherit;
  font-size: 0.6em;
  font-weight: bold;
  padding: 0.5em 1em;
  color: #d8cdb4;
  cursor: pointer;
  white-space: nowrap;
  transition: color 150ms, border-color 150ms, background 150ms;

  // FT-1083: `:not(.on)` — a LIT control keeps its lit dress under the
  // cursor. Measured while proving this lane: `.vo-btn:hover:not(.disabled)`
  // outranks `.vo-mark.on` on specificity, so pressing the execution mark
  // repainted it straight back to the plate it already wore — and since your
  // pointer is by definition still on the button you just pressed, the lit
  // state was invisible at the exact moment it was earned. That is half of
  // why the mark read as having no undo: it did not visibly read as having a
  // DO either. `.on` appears on no other `.vo-btn`, so nothing else moves.
  &:hover:not(.disabled):not(.on) {
    color: #fff;
    @include control-plate-hover;
  }
  &:focus-visible {
    @include control-focus-ring;
  }
  &.disabled {
    @include control-disabled;
  }
  @media (pointer: coarse) {
    min-height: 40px;
    padding: 0.5em 1.1em;
  }
}

// THE PRIMARY WEIGHT (FT-1074: shared by "Start the vote" and "Record vote",
// never both at once — the template demotes Start while a completed sweep
// waits). Emphatic, and never blood, because blood is `control-lit` here and
// this control is not a thing that is switched on. The emphasis is the
// plate's own parchment ink promoted to the edge, so it is the brightest
// object on the surface without introducing a colour.
.vo-btn.is-primary {
  background: rgba(60, 50, 32, 0.88);
  border-color: rgba(216, 205, 180, 0.8);
  color: #fdf6e6;
  box-shadow: 0 0 14px rgba(216, 205, 180, 0.16);
  font-size: 0.68em;
  padding: 0.55em 1.3em;

  &:hover:not(.disabled) {
    background: rgba(84, 70, 43, 0.94);
    border-color: #efe3c6;
    color: #fff;
  }
}
// FT-1378 (user): the START button is PURPLE, not the gold — FT-1108's
// chosen-purple pair (the settings rail's lit leaf), scoped to the start
// control alone; Record vote keeps the gold primary.
.vo-btn.vo-start.is-primary {
  background: rgba(96, 74, 128, 0.55);
  border-color: rgba(167, 143, 205, 0.85);
  color: #ece4f8;
  box-shadow: 0 0 14px rgba(167, 143, 205, 0.2);

  &:hover:not(.disabled) {
    background: rgba(112, 88, 148, 0.65);
    border-color: rgba(190, 168, 224, 0.95);
    color: #fff;
  }
}
// FT-1378: the in-button scrub wears the label's own size — the button is
// one sentence, not a sentence with a footnote.
.vo-btn.vo-start {
  display: inline-flex;
  align-items: baseline;
  gap: 0.5em;

  .vo-timing-in {
    font-size: inherit;
    color: inherit;

    .vo-scrub {
      font-size: inherit;
    }
  }
}

// THE QUIET WEIGHT. "Cancel nomination" is the walk-away — small, dim, still
// a plated control (it keeps hover, focus and its title), just last in the
// room's attention. When the same button is about to WRITE the result it is
// `.is-primary` above and none of this applies.
.vo-finish:not(.is-primary) {
  font-size: 0.5em;
  color: #b3a88f;
  background: rgba(0, 0, 0, 0.35);
  border-color: rgba(216, 205, 180, 0.22);
}

// ── A PLAYER'S OWN HAND ─────────────────────────────────────────────────────
// One state, two positions: the plate on the group, cells inside it, lit on
// whichever is true. Same shape as the night-mode switch.
.vo-hands {
  @include control-plate;
  display: inline-flex;
  overflow: hidden;
}
// FT-1311 item 2 tunes the pair, small: the resting cells calm down to the
// app's parchment ink (control-cell's plain white was the loudest thing on
// the strip at rest), the glyph dims with its cell and wakes with it, and
// the lit cell earns a real edge — a sunken well plus a purple keyline — so
// "which way is my hand" reads from silhouette + ground, not hue alone.
// Hand DOWN stays words alone (user call 2026-08-28: ONE glyph for the pair).
.vo-hand {
  @include control-cell;
  font-size: 0.6em;
  font-weight: bold;
  padding: 0.5em 1.1em;
  min-height: 2.9em;
  white-space: nowrap;
  transition: color 150ms, background 150ms, box-shadow 150ms;
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  color: #cfc4ad;

  /* The Hand UP glyph is the nominate manicule's own painted art
     (ui-nominate-hand.png), rotated upright — the art points right in
     file. Art, not a tinted mask, so it looks exactly like the coin's. */
  .vo-hand-ic {
    width: 1.4em;
    height: 1.4em;
    flex: 0 0 auto;
    opacity: 0.75;
    transition: opacity 150ms;
  }
  .vo-hand-nom {
    background: url("../assets/ui-nominate-hand.png") center / contain
      no-repeat;
    /* the art points LEFT in file — +90 stands it up */
    transform: rotate(90deg);
  }

  &:hover:not(.on) {
    background: rgba(var(--reader-ground-rgb), 0.95);
    color: #fff;
    .vo-hand-ic {
      opacity: 1;
    }
  }
  &.on {
    /* User call 2026-08-28: the lit cell wears the pick purple (the claim
       ask's #a78fcd family) instead of the control-on ink. */
    background: rgba(167, 143, 205, 0.28);
    color: #e8dcfb;
    box-shadow:
      inset 0 0 0 1px rgba(167, 143, 205, 0.55),
      inset 0 2px 6px rgba(0, 0, 0, 0.5);
    .vo-hand-ic {
      opacity: 1;
    }
  }
  @media (pointer: coarse) {
    min-height: 40px;
  }
}

// FT-1311 item 3: once the sweep has passed my seat (or I have no vote to
// raise) the pair FREEZES instead of vanishing — the lit cell keeps saying
// which way my hand stands, the dead cell drops back, and nothing reflows.
// vote() already refuses the click (canVote guards it); this makes the
// refusal visible.
.vo-hands.is-locked {
  pointer-events: none;
  .vo-hand:not(.on) {
    color: rgba(216, 205, 180, 0.35);
    .vo-hand-ic {
      opacity: 0.3;
    }
  }
}

.vo-hint {
  margin: 0;
  font-size: 0.6em;
  color: #cfc4ad;
}

// ── THE DOCKED STRIP (FT-1074) ──────────────────────────────────────────────
// The card's mid-vote form: while the hands sweep, the face stays open and
// this compact strip sits at the bottom of the circle (--vo-dock-y, declared
// on #vote). It comes AFTER the .overlay base rule so its declarations win:
// the scrim's ::before stands down (`content: none`), the column becomes a
// row, and the ground is a plain control plate — the app's engraved-plate
// idiom at strip size, in both registers (the disc gate above never matches
// it: `:not(.vo-docked)`).
// FT-1382: the player's bare pair — fixed at the face centre, no plate of
// its own (the buttons carry their own chrome). The locked state dims the
// whole pair exactly as the card's hands did.
.vo-solo {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 21;
  // User call 2026-09-11 (live look): the pair reads too small over the
  // knocker — ~1.6x, so it is the thing a seated player's eye lands on.
  font-size: 3.2em;

  &.is-locked {
    opacity: 0.55;
  }
}
.vo-docked {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) translateY(var(--vo-dock-y));
  width: auto;
  flex-direction: row;
  gap: 0.7em;
  // FT-1082b (user): the docked strip is LOUDER — the purple plate the mark
  // button wears, a heavier ground and a lit edge, so a strip at the rim
  // still reads as the town's live tally.
  padding: 0.55em 1.1em;
  @include control-plate;
  background: rgba(var(--reader-ground-rgb), 0.95);
  border-color: rgba(var(--reader-rgb), 0.75);
  box-shadow: 0 0 14px rgba(0, 0, 0, 0.75),
    0 0 0 1px rgba(var(--reader-rgb), 0.35);

  &:before {
    content: none;
  }

  // Tally still first, still big — for the strip. One line, no subline.
  .vo-tally {
    flex-direction: row;
    align-items: baseline;
    gap: 0.12em;
    .vo-now {
      font-size: 1.7em;
    }
    .vo-slash {
      font-size: 1em;
    }
    .vo-need {
      font-size: 1.1em;
    }
  }
}

// ── THE DOCK TRANSITION ─────────────────────────────────────────────────────
// Transform and opacity only, `mode="out-in"`: the full card shrinks away
// DOWNWARD — toward the dock, so the two legs read as one journey — then the
// strip rises the last inch into place. The desktop card's leg is restated
// inside the disc gate above, where the frame's own centring translate has to
// stay in the chain.
.vo-dock-enter-active,
.vo-dock-leave-active {
  transition:
    transform 240ms ease,
    opacity 240ms ease;
}
.overlay:not(.vo-docked) {
  &.vo-dock-enter,
  &.vo-dock-enter-from,
  &.vo-dock-leave-to {
    opacity: 0;
    transform: translateY(calc(var(--vo-dock-y) * 0.55)) scale(0.6);
  }
}
.vo-docked {
  &.vo-dock-enter,
  &.vo-dock-enter-from,
  &.vo-dock-leave-to {
    opacity: 0;
    transform: translate(-50%, -50%) translateY(calc(var(--vo-dock-y) + 1.2em))
      scale(0.92);
  }
}
@media (prefers-reduced-motion: reduce) {
  .vo-dock-enter-active,
  .vo-dock-leave-active {
    transition: none;
  }
}

// ── THE CLOCK HANDS ─────────────────────────────────────────────────────────
// Each hand's art swings in from half a turn away as it fades up. The swing
// runs on the art layer (::before), never on .needle itself — the needle's
// own transform is the inline pointing angle.
@keyframes needle-swing-cw {
  from {
    opacity: 0;
    transform: rotate(-0.5turn);
  }
  to {
    opacity: 1;
    transform: rotate(0);
  }
}
@keyframes needle-swing-ccw {
  from {
    opacity: 0;
    transform: rotate(0.5turn);
  }
  to {
    opacity: 1;
    transform: rotate(0);
  }
}

// The frame the hand art is drawn to: pivot at its centre (#vote's centre,
// no insets). FT-1444 (user): the hands ~25% bigger — 187.5% of #vote's
// height by 31.25% of its width (was 150% / 25%, where the tips just reached
// the seat ring). #vote centres it, so the pivot does not move. No clicks.
.arrows {
  position: absolute;
  block-size: 187.5%;
  inline-size: 31.25%;
  pointer-events: none;

  .needle {
    position: absolute;
    inset: 0;
    // The real duration arrives inline (one pace step); this is a fallback.
    transition: transform 2.5s ease-in-out;

    &::before {
      content: "";
      display: block;
      position: absolute;
      inset: 0;
      background: no-repeat center / auto 100%;
      filter: drop-shadow(0 0 3px #000);
    }
  }

  // FT-1075: the hands are OURS now — crafted SVGs in the fork's own painted
  // language (the manicule precedent), replacing upstream's filigree PNGs
  // (clock-small/clock-big, retired in place). Same canvas, same pivot row,
  // same reach — each file's header states the mounting contract — so the
  // sweep math above and every rule in this block are untouched: assets
  // swapped, geometry kept. Blue ($townsfolk) marks the nominator, red
  // ($demon) the nominee, the pair drawn to the tower's own gothic dial
  // silhouettes — spearhead, tapered pierced shaft, turned boss, spade tail.
  .needle-nominator::before {
    background-image: url("../assets/vote-hand-nominator.svg");
    animation: needle-swing-ccw 1.1s ease-out;
  }
  .needle-nominee::before {
    background-image: url("../assets/vote-hand-nominee.svg");
    animation: needle-swing-cw 1.1s ease-out;
  }
}

.countdown {
  position: absolute;
  display: flex;
  place-content: center;
  place-items: center;
  pointer-events: none;
  // FT-1083 (user: "the numbers should be above the hands"). The three
  // siblings under #vote paint in a ruled order, not a drifted one: the card
  // is 1, the sweeping hands are 2 (`.arrows.vo-live`, inside the face-disc
  // gate above — FT-1024b put them over the plate, FT-1075 scoped that to the
  // sweep), and the countdown is 3, over both, in both registers. It cannot
  // reach across FT-1075's ruling because it does not coexist with either
  // state it governs: `isVoteInProgress && !lockedVote` is true only for the
  // three seconds between Start and the first locked hand — the card has
  // already docked to the rim, the sweep has not begun — and this layer takes
  // no clicks (`pointer-events: none` above), so nothing beneath it ever
  // loses one.
  z-index: 3;
}

// ── THE COUNTDOWN, IN OUR NUMERALS (FT-1083) ────────────────────────────────
// Written AFTER the block above so its declarations win — the same ordering
// the docked strip uses to stand down the card's scrim. `.countdown .vo-beat`
// is (0,2,0) against the retired `.countdown span`'s (0,1,1), and the
// per-beat `.vo-beat:nth-child(n)` is (0,3,0) against (0,2,1), so every
// property upstream set is overridden rather than merely added to.
//
// THE MATERIAL is FaceHands' `.tw-numeral`, quoted rather than re-invented:
// Times bold, sized in FACE-PIXELS (--fpx, published on #app) because this
// stands on the painted dial and has to hold its proportion of the face at
// every window size, and the same shadow stack — including the live `--ng-*`
// reads, so a turn of the numeral-glow lab moves the ring and the countdown
// together and they can never drift into two dressings.
//
// THE INK (FT-1438, user): the ring's own near-black, not the ember-gold of
// FT-1083 — see the rule below.
$vo-beat-size: 118; // the beat's cap size in face-pixels — judged on the face
.countdown .vo-beat {
  position: absolute;
  font-family: "Times New Roman", Times, serif;
  font-weight: bold;
  font-size: calc(#{$vo-beat-size} * var(--fpx));
  line-height: 1;
  letter-spacing: calc(4 * var(--fpx));
  // letter-spacing adds a trailing gap after the LAST glyph, which widens the
  // box and so shifts "III" a couple of face-pixels left of the pivot; the
  // negative margin takes that gap back out of the box's own width.
  margin-right: calc(-4 * var(--fpx));
  // FT-1438 (user: "use our black numerals, not these yellow ones"): the
  // ring's own ink and dressing — FaceHands .tw-numeral's near-black and its
  // --ng-* shadow stack, every radius scaled to this glyph (118 / 34 ≈ 3.5x),
  // so the countdown reads as the clock's own numerals counting down.
  color: #0a0502;
  opacity: 0;
  text-shadow:
    0 calc(3.5 * var(--fpx)) calc(3.5 * var(--fpx))
      rgba(255, 250, 235, calc(var(--ng-under, 16) / 100)),
    0 calc(-3.5 * var(--fpx)) calc(3.5 * var(--fpx))
      rgba(10, 5, 2, calc(var(--ng-top, 0) / 100)),
    0 calc(7 * var(--fpx)) calc(var(--ng-drop-blur, 1) * 3.5 * var(--fpx))
      rgba(0, 0, 0, calc(var(--ng-drop, 40) / 100)),
    0 0 calc(var(--ng-halo-size, 0) * 3.5 * var(--fpx))
      rgba(255, 246, 220, calc(var(--ng-halo, 0) / 100)),
    0 0 calc(var(--ng-breath-size, 3) * 3.5 * var(--fpx))
      rgba(255, 240, 200, calc(var(--ng-breath, 69) / 100));
}

// GO is the release, not a count: hot ember rather than the counts' gold, and
// the only beat that GROWS on the way out instead of settling. Ember, not the
// pale salmon the first shot produced — a light red on a lit ochre face reads
// as washed-out gold, which is the one thing this beat must not read as.
.countdown .vo-beat-go {
  color: #ff6a3c;
  text-shadow:
    0 calc(3 * var(--fpx)) calc(3 * var(--fpx))
      rgba(255, 236, 226, calc(var(--ng-under, 16) / 100)),
    0 calc(7 * var(--fpx)) calc(var(--ng-drop-blur, 1) * 4 * var(--fpx))
      rgba(0, 0, 0, calc(var(--ng-drop, 40) / 100)),
    0 0 calc(2 * var(--fpx)) rgba(8, 2, 1, 0.95),
    0 0 calc(5 * var(--fpx)) rgba(8, 2, 1, 0.9),
    0 0 calc(10 * var(--fpx)) rgba(22, 4, 2, 0.64),
    0 0 calc(30 * var(--fpx)) rgba(240, 70, 32, 0.72),
    0 0 calc(66 * var(--fpx)) rgba(190, 18, 8, 0.55);
}

// TRANSFORM AND OPACITY ONLY — no blur filter, no animated colour, the
// discipline the dock transition above already keeps. A beat lands slightly
// over its size and settles, holds, then lets go a hair smaller: the same
// shape as a struck bell.
@keyframes vo-beat {
  0% {
    opacity: 0;
    transform: scale(1.34);
  }
  14% {
    opacity: 1;
    transform: scale(1.04);
  }
  30% {
    transform: scale(1);
  }
  72% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.93);
  }
}

@keyframes vo-beat-go {
  0% {
    opacity: 0;
    transform: scale(0.7);
  }
  16% {
    opacity: 1;
    transform: scale(1.08);
  }
  34% {
    transform: scale(1);
  }
  68% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(1.45);
  }
}

// The cadence is untouched — 1100ms a beat, one second apart, GO on the
// third — because countdown() upstairs waits exactly 4000ms before starting
// the sweep and the audio track is cut to the same bar.
.countdown .vo-beat:nth-child(1) {
  animation: vo-beat 1100ms normal forwards;
}
.countdown .vo-beat:nth-child(2) {
  animation: vo-beat 1100ms normal forwards 1000ms;
}
.countdown .vo-beat:nth-child(3) {
  animation: vo-beat 1100ms normal forwards 2000ms;
}
.countdown .vo-beat:nth-child(4) {
  animation: vo-beat-go 1100ms normal forwards 3000ms;
}

// Reduced motion keeps the COUNT — it is information, not decoration — and
// drops the scale, so each numeral simply appears and goes.
@media (prefers-reduced-motion: reduce) {
  @keyframes vo-beat {
    0%,
    100% {
      opacity: 0;
    }
    10%,
    80% {
      opacity: 1;
    }
  }
  @keyframes vo-beat-go {
    0%,
    100% {
      opacity: 0;
    }
    10%,
    80% {
      opacity: 1;
    }
  }
}
</style>
